/**
 * Ein Nicht-Ereignis bekommt keine Meldung. (22.09.2026)
 *
 * ## Woher das kommt
 *
 * Martin, mit einem Foto vom Telefon: *„Hier muss auch noch mal schöner
 * gemacht werden, beziehungsweise diese Information ist im Smartphone-Modus
 * schrecklich. Da brauche ich ja die ganze Information gar nicht."*
 *
 * Auf dem Bild stand, quer über den halben Bildschirm:
 *
 *     Nichts Neues — 150 Einträge, alle schon da
 *     Vespera liest den Verlauf ohnehin bei jedem Zug selbst.
 *
 * Zwei Zeilen, um zu sagen, dass nichts passiert ist.
 *
 * ## Warum der Satz überhaupt entstanden ist
 *
 * Der Nachsatz war eine Erklärung für den Knopf „Verlauf neu vom Server":
 * er sollte verhindern, dass jemand ihn für ein Gedächtnis hält, das er
 * nicht ist. Diesen Knopf gibt es seit heute nicht mehr in der Symbol-Leiste
 * (Martin: *„den drücke ich nie"*) — die Erklärung erklärt also etwas, das
 * niemand mehr sieht.
 *
 * ## Die Regel
 *
 * - **Nichts Neues:** gar nichts zeigen. Wer nichts geladen hat, muss das
 *   nicht lesen.
 * - **Etwas Neues:** die Zahl, sonst nichts. „3 neu" ist die ganze Auskunft.
 * - **Etwas schiefgegangen:** vollständig melden. Das ist keine Kosmetik,
 *   sondern der einzige Fall, in dem jemand etwas erfahren muss.
 */

interface VerlaufZahlen {
  /** Wie viele Einträge neu dazukamen. */
  neu: number;
  /** Wie viele schon da waren. */
  schonDa: number;
  /** Wie viele nicht dargestellt werden konnten. */
  uebersprungen: number;
}

interface VerlaufMeldung {
  text: string;
  /** Die zweite, leisere Zeile — nur wo sie wirklich etwas beiträgt. */
  nachsatz?: string;
}

/**
 * Was nach dem Laden des Verlaufs angezeigt wird — oder `null` für nichts.
 */
export function verlaufMeldung(z: VerlaufZahlen): VerlaufMeldung | null {
  /*
   * ⚠️ Der Fehlerfall zuerst. Er ist der einzige, der auch dann gezeigt
   *    wird, wenn sonst nichts passiert ist: ein übersprungener Eintrag ist
   *    eine Nachricht, die jemand geschrieben hat und nicht sieht.
   */
  if (z.uebersprungen > 0) {
    return {
      text: `${z.uebersprungen} ${z.uebersprungen === 1 ? "Eintrag" : "Einträge"} konnten nicht angezeigt werden`,
      nachsatz: "Sie sind nicht verloren — sie stehen weiter im Archiv.",
    };
  }
  // Nichts Neues ist ein Nicht-Ereignis.
  if (z.neu <= 0) return null;
  /*
   * ⚠️⚠️ 22.09.2026, NACHTRAG — DAS ERSTE LADEN IST AUCH KEIN EREIGNIS.
   *
   * Martin, mit einem Foto der Pille „50 neu": *„was bedeutet diese Zahl,
   * bitte ein einfach laden genügt!"*
   *
   * Er hat recht, und es ist mein Fehler vom selben Tag: Ich hatte die
   * Meldung auf „N neu" gekürzt und dabei nur ans NACHLADEN gedacht. Beim
   * ersten Öffnen ist aber jede Nachricht neu — die Zahl sagt dann nur, wie
   * viele geladen wurden, und das weiß niemand zu deuten. Seit pre.3150
   * sind es genau 50, also stand dort dauerhaft „50 neu".
   *
   * Woran das erste Laden zu erkennen ist: es lag noch nichts da. Kamen
   * Einträge dazu, die schon bekannt waren, ist es ein Nachladen — und dann
   * ist „3 neu" eine echte Auskunft.
   */
  if (z.schonDa <= 0) return null;
  return { text: `${z.neu} neu` };
}
